import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutlined,
  CodeOutlined,
  ContentCopyOutlined,
  ErrorOutlineRounded,
  OpenInNewOutlined,
  VisibilityOutlined,
  AutoAwesomeOutlined,
} from '@mui/icons-material';

import useApiCall from '../../hooks/useApiCall';
import useToast from '../../hooks/useToast';
import { TOAST } from '../../constants/ToastMessages';
import { usePageMeta } from '../../contexts/PageMetaContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import BottomActionBar from '../../components/BottomActionBar';
import VariableChip from '../../components/VariableChip';
import PromptVariableAutocomplete from '../../components/PromptVariableAutocomplete/PromptVariableAutocomplete';
import { INDIGO, SLATE } from '../../constants/Colors';

// ─── Constants ────────────────────────────────────────────────────────────────
const PANEL_H = 'calc(100vh - 200px)';

const CARD_SX = {
  bgcolor: '#fff',
  border: `1px solid ${SLATE[200]}`,
  borderRadius: '12px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function extractVarNames(text) {
  if (!text) return [];
  return [...new Set([...text.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]))];
}

function resolvePrompt(text, variableMap) {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (_, name) =>
    variableMap[name] !== undefined ? String(variableMap[name]) : `{{${name}}}`
  );
}

// ─── Section header for variable groups ───────────────────────────────────────
function VarGroupHeader({ label, count, resolved }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: '6px',
        bgcolor: resolved ? '#f0fdf4' : '#fffbeb',
        borderTop: `1px solid ${SLATE[100]}`,
        borderBottom: `1px solid ${resolved ? '#bbf7d0' : '#fde68a'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: resolved ? '#15803d' : '#92400e',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          px: '5px',
          py: '1px',
          bgcolor: resolved ? '#dcfce7' : '#fef3c7',
          border: `1px solid ${resolved ? '#86efac' : '#fcd34d'}`,
          borderRadius: '4px',
        }}
      >
        <Typography
          sx={{ fontSize: '0.625rem', fontWeight: 700, color: resolved ? '#15803d' : '#92400e' }}
        >
          {count}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Variable row ─────────────────────────────────────────────────────────────
function VarRow({ varName, value }) {
  const hasValue = value !== undefined && value !== '';
  const raw = hasValue ? String(value) : null;
  const preview = raw ? (raw.length > 60 ? raw.slice(0, 60) + '…' : raw) : null;

  return (
    <Box
      sx={{
        px: 2,
        py: '10px',
        borderBottom: `1px solid ${SLATE[100]}`,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: SLATE[50] },
        transition: 'background 0.12s',
      }}
    >
      {/* Row 1: name chip + status icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '5px', minWidth: 0 }}>
        <Tooltip title={`{{${varName}}}`} placement="top">
          <Box
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'inline-flex',
              alignItems: 'center',
              px: '7px',
              py: '2px',
              bgcolor: INDIGO[50],
              border: `1px solid ${INDIGO[200]}`,
              borderRadius: '5px',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontFamily: 'monospace',
                fontWeight: 600,
                color: INDIGO[600],
                letterSpacing: '0.01em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {`{{${varName}}}`}
            </Typography>
          </Box>
        </Tooltip>
        {hasValue ? (
          <CheckCircleOutlined sx={{ fontSize: '14px', color: '#22c55e', flexShrink: 0 }} />
        ) : (
          <ErrorOutlineRounded sx={{ fontSize: '14px', color: '#f59e0b', flexShrink: 0 }} />
        )}
      </Box>

      {/* Row 2: value preview */}
      {hasValue ? (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: SLATE[500],
            fontFamily: "'SF Mono','Consolas',monospace",
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {preview}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: '0.75rem', color: '#b45309', lineHeight: 1.4 }}>
          No value — won't resolve
        </Typography>
      )}
    </Box>
  );
}

// ─── Preview: chips view ──────────────────────────────────────────────────────
function ChipPreview({ text, variableMap }) {
  if (!text)
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.875rem', color: SLATE[400] }}>
          No prompt written yet.
        </Typography>
      </Box>
    );
  return (
    <Box
      sx={{
        fontSize: '0.9rem',
        lineHeight: 2.1,
        color: SLATE[800],
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text.split(/(\{\{\w+\}\})/g).map((part, i) => {
        const m = part.match(/^\{\{(\w+)\}\}$/);
        if (!m) return <span key={i}>{part}</span>;
        const val = variableMap[m[1]];
        return (
          <Tooltip
            key={i}
            title={
              val !== undefined
                ? `→ ${String(val).slice(0, 120)}`
                : 'No value set for this variable'
            }
            placement="top"
            arrow
          >
            <span style={{ display: 'inline-flex', verticalAlign: 'middle', margin: '0 2px' }}>
              <VariableChip name={m[1]} />
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
}

// ─── Resolved: substituted values view ───────────────────────────────────────
function ResolvedView({ text, variableMap, onCopy, copied }) {
  if (!text)
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.875rem', color: SLATE[400] }}>
          No prompt to resolve.
        </Typography>
      </Box>
    );

  const parts = text.split(/(\{\{\w+\}\})/g);
  const hasMissing = parts.some(p => {
    const m = p.match(/^\{\{(\w+)\}\}$/);
    return m && variableMap[m[1]] === undefined;
  });

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeOutlined sx={{ fontSize: '0.875rem', color: INDIGO[500] }} />
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[700] }}>
            Resolved Prompt
          </Typography>
          {hasMissing && (
            <Box
              sx={{
                px: '8px',
                py: '2px',
                bgcolor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ErrorOutlineRounded sx={{ fontSize: '0.6875rem', color: '#d97706' }} />
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#92400e' }}>
                Some variables missing
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title={copied ? 'Copied!' : 'Copy resolved prompt'} placement="top">
          <IconButton
            size="small"
            onClick={onCopy}
            sx={{
              color: copied ? INDIGO[600] : SLATE[400],
              bgcolor: copied ? INDIGO[50] : 'transparent',
              border: `1px solid ${copied ? INDIGO[200] : SLATE[200]}`,
              borderRadius: '8px',
              p: '6px',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: INDIGO[50], color: INDIGO[600], borderColor: INDIGO[200] },
            }}
          >
            <ContentCopyOutlined sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Resolved text */}
      <Box
        sx={{
          fontSize: '0.9rem',
          lineHeight: 2,
          color: SLATE[800],
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {parts.map((part, i) => {
          const m = part.match(/^\{\{(\w+)\}\}$/);
          if (!m) return <span key={i}>{part}</span>;
          const name = m[1];
          const value = variableMap[name];

          if (value !== undefined) {
            // Resolved — highlight value inline
            const display =
              String(value).length > 200 ? String(value).slice(0, 200) + '…' : String(value);
            return (
              <Tooltip key={i} title={`{{${name}}}`} placement="top">
                <Box
                  component="span"
                  sx={{
                    bgcolor: '#eef2ff',
                    color: INDIGO[700],
                    px: '5px',
                    borderRadius: '4px',
                    fontWeight: 500,
                    cursor: 'default',
                  }}
                >
                  {display}
                </Box>
              </Tooltip>
            );
          }

          // Missing — show placeholder
          return (
            <Tooltip key={i} title="No value set for this variable" placement="top">
              <Box
                component="span"
                sx={{
                  bgcolor: '#fff7ed',
                  color: '#c2410c',
                  px: '5px',
                  borderRadius: '4px',
                  fontFamily: "'SF Mono','Consolas',monospace",
                  fontSize: '0.8rem',
                  border: '1px dashed #fed7aa',
                  cursor: 'default',
                }}
              >
                [{name}]
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Stat pill ────────────────────────────────────────────────────────────────
function Stat({ value, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[700] }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: SLATE[400] }}>{label}</Typography>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SystemPrompt() {
  const { id: agentId, app_name } = useParams();
  const navigate = useNavigate();
  const tokens = useSelector(state => state?.tokens?.value);
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { success } = useToast();
  const { setPageMeta } = usePageMeta();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [tab, setTab] = useState(0); // 0=editor 1=preview 2=resolved
  const [variableMap, setVariableMap] = useState({});
  const [copied, setCopied] = useState(false);
  const originalRef = useRef('');

  useEffect(() => () => setPageMeta({}), []); // eslint-disable-line

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [promptRes, varsRes] = await Promise.all([
        makeRequest(`/v1/apps/${currentApp?.id}/agents/${agentId}/files/system_instructions`, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest(`/v1/apps/${currentApp?.id}/agents/${agentId}/files/prompt_variables`, {
          method: 'GET',
          authTokens: tokens,
        }),
      ]);
      const sp = promptRes?.file_data?.system_instructions || '';
      setPrompt(sp);
      originalRef.current = sp;
      setVariableMap(varsRes?.file_data || {});
    } catch {
      // toasted by useApiCall
    } finally {
      setLoading(false);
    }
  }, [agentId, currentApp?.id]); // eslint-disable-line

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await makeRequest(`/v1/apps/${currentApp?.id}/agents/${agentId}/files/system_instructions`, {
        method: 'PUT',
        body: { content: prompt },
        authTokens: tokens,
      });
      originalRef.current = prompt;
      success(TOAST.instructions.saved);
    } catch {
      // toasted
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(resolvePrompt(prompt, variableMap)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const detectedVars = extractVarNames(prompt);
  const resolvedVars = detectedVars.filter(n => variableMap[n] !== undefined && variableMap[n] !== '');
  const missingVars = detectedVars.filter(n => !variableMap[n]);
  const wordCount = prompt.trim() ? prompt.trim().split(/\s+/).length : 0;
  const isDirty = prompt !== originalRef.current;
  const isReadOnly = ['stage', 'production'].includes(process.env.REACT_APP_CURRENT_ENVIRONMENT);

  if (loading) return <LoadingSpinner message="Loading prompt…" />;

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 2.5, alignItems: 'flex-start', pb: 10 }}>
      {/* ── Left: Editor panel ──────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, height: PANEL_H, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ ...CARD_SX, flex: 1, minHeight: 0 }}>
          {/* Tab bar */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              borderBottom: `1px solid ${SLATE[100]}`,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 46,
                '& .MuiTab-root': {
                  minHeight: 46,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  fontWeight: 500,
                  color: SLATE[400],
                  px: '16px',
                  gap: '5px',
                  '&.Mui-selected': { color: SLATE[900], fontWeight: 600 },
                },
                '& .MuiTabs-indicator': {
                  bgcolor: INDIGO[600],
                  height: '2px',
                  borderRadius: '1px',
                },
              }}
            >
              <Tab
                icon={<CodeOutlined sx={{ fontSize: '0.875rem' }} />}
                iconPosition="start"
                label="Editor"
              />
              <Tab
                icon={<VisibilityOutlined sx={{ fontSize: '0.875rem' }} />}
                iconPosition="start"
                label="Preview"
              />
              <Tab
                icon={<AutoAwesomeOutlined sx={{ fontSize: '0.875rem' }} />}
                iconPosition="start"
                label="Resolved"
              />
            </Tabs>

            {/* Missing badge */}
            {missingVars.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  px: '10px',
                  py: '5px',
                  bgcolor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '6px',
                }}
              >
                <ErrorOutlineRounded sx={{ fontSize: '0.6875rem', color: '#d97706' }} />
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#92400e' }}>
                  {missingVars.length} variable{missingVars.length !== 1 ? 's' : ''} missing
                </Typography>
              </Box>
            )}
          </Box>

          {/* Body (scrollable) */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {tab === 0 && (
              <PromptVariableAutocomplete
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                variables={Object.keys(variableMap)}
                textFieldProps={{
                  fullWidth: true,
                  multiline: true,
                  minRows: 26,
                  variant: 'outlined',
                  disabled: isReadOnly,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      fontSize: '0.9rem',
                      lineHeight: 1.9,
                      fontFamily: "'SF Mono','Fira Code','Consolas',monospace",
                      color: SLATE[800],
                      bgcolor: '#fff',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputBase-input': { px: 3, py: 2.5 },
                    '& .MuiFormHelperText-root': { display: 'none' },
                  },
                }}
              />
            )}

            {tab === 1 && (
              <Box sx={{ px: 3, py: 2.5 }}>
                <ChipPreview text={prompt} variableMap={variableMap} />
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ px: 3, py: 2.5 }}>
                <ResolvedView
                  text={prompt}
                  variableMap={variableMap}
                  onCopy={handleCopy}
                  copied={copied}
                />
              </Box>
            )}
          </Box>

          {/* Status bar */}
          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              py: '9px',
              borderTop: `1px solid ${SLATE[100]}`,
              bgcolor: SLATE[50],
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Stat value={wordCount} label="words" />
            <Stat value={prompt.length} label="chars" />
            <Stat value={detectedVars.length} label="variables" />
            {detectedVars.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SLATE[300] }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: 500 }}>
                  {resolvedVars.length} resolved
                </Typography>
                {missingVars.length > 0 && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 500 }}>
                    · {missingVars.length} missing
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box
                sx={{
                  px: '6px',
                  py: '2px',
                  bgcolor: '#fff',
                  border: `1px solid ${SLATE[200]}`,
                  borderRadius: '5px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: SLATE[600],
                    fontFamily: 'monospace',
                  }}
                >
                  {'{'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: SLATE[400] }}>
                to insert a variable
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Right: Variable inspector ──────────────────────────────────── */}
      <Box
        sx={{
          width: 268,
          flexShrink: 0,
          height: PANEL_H,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ ...CARD_SX, flex: 1, minHeight: 0 }}>
          {/* Header */}
          <Box
            sx={{
              flexShrink: 0,
              px: 2.5,
              py: '14px',
              borderBottom: `1px solid ${SLATE[100]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: SLATE[900] }}>
                Variables
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: SLATE[400], mt: '1px' }}>
                {detectedVars.length === 0
                  ? 'None detected yet'
                  : `${detectedVars.length} in prompt`}
              </Typography>
            </Box>
            {detectedVars.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Tooltip title="Resolved" placement="top">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <CheckCircleOutlined sx={{ fontSize: '0.875rem', color: '#22c55e' }} />
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[700] }}>
                      {resolvedVars.length}
                    </Typography>
                  </Box>
                </Tooltip>
                {missingVars.length > 0 && (
                  <>
                    <Typography sx={{ fontSize: '0.75rem', color: SLATE[300] }}>/</Typography>
                    <Tooltip title="Missing" placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <ErrorOutlineRounded sx={{ fontSize: '0.875rem', color: '#f59e0b' }} />
                        <Typography
                          sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[700] }}
                        >
                          {missingVars.length}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Variable list */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {detectedVars.length === 0 ? (
              <Box sx={{ py: 7, px: 3, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: SLATE[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.5,
                  }}
                >
                  <CodeOutlined sx={{ fontSize: '1.125rem', color: SLATE[400] }} />
                </Box>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: SLATE[600] }}>
                  No variables yet
                </Typography>
                <Typography
                  sx={{ fontSize: '0.75rem', color: SLATE[400], mt: '6px', lineHeight: 1.6 }}
                >
                  Type {'{{ '} in the editor to reference a variable
                </Typography>
              </Box>
            ) : (
              <>
                {resolvedVars.length > 0 && (
                  <>
                    <VarGroupHeader label="Resolved" count={resolvedVars.length} resolved />
                    {resolvedVars.map(name => (
                      <VarRow key={name} varName={name} value={variableMap[name]} />
                    ))}
                  </>
                )}
                {missingVars.length > 0 && (
                  <>
                    <VarGroupHeader label="Missing" count={missingVars.length} resolved={false} />
                    {missingVars.map(name => (
                      <VarRow key={name} varName={name} value={variableMap[name]} />
                    ))}
                  </>
                )}
              </>
            )}
          </Box>

          {/* Footer */}
          {detectedVars.length > 0 && (
            <Box sx={{ flexShrink: 0, p: '12px', borderTop: `1px solid ${SLATE[100]}` }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                endIcon={<OpenInNewOutlined sx={{ fontSize: '0.8125rem' }} />}
                onClick={() => navigate(`/${app_name}/agent/${agentId}/instructions`)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: SLATE[600],
                  borderColor: SLATE[200],
                  borderRadius: '8px',
                  py: '8px',
                  '&:hover': { bgcolor: INDIGO[50], color: INDIGO[600], borderColor: INDIGO[200] },
                }}
              >
                Edit variable values
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {!isReadOnly && (
        <BottomActionBar
          name="Save Prompt"
          handleSave={handleSave}
          saving={saving}
          isDirty={isDirty}
        />
      )}
    </Box>
  );
}
